import React, { ReactNode } from "react";
import { Tabs as AntdMobileTabs, Badge as AntdMobileBadge } from "antd-mobile";
import { BaseTabsProps, BaseTabProps } from "./Tabs.types";
import "./Tabs.scss";
import Badge from "../Badge";

/**
 * Badge需要包裹children才能正确显示
 */
const renderTitleWithBadge = (
  title: ReactNode,
  badgeContent?: ReactNode | number | boolean
) => {
  if (badgeContent === undefined || badgeContent === null) {
    return title;
  }

  // Badge必须包裹内容才能显示
  return (
    <Badge
      content={badgeContent === true ? AntdMobileBadge.dot : badgeContent}
      dot={badgeContent === true}
      className="tab-title-badge"
    >
      <span className="tab-title-text">{title}</span>
    </Badge>
  );
};

export const Tab: React.FC<BaseTabProps> = ({
  children,
  title,
  badgeContent,
  ...props
}) => {
  const content = children !== undefined ? children : <div />;

  return (
    <AntdMobileTabs.Tab
      title={renderTitleWithBadge(title, badgeContent)}
      {...props}
    >
      {content}
    </AntdMobileTabs.Tab>
  );
};

const TabsComponent: React.FC<BaseTabsProps> = ({
  children,
  items,
  variant = "primary",
  size = "md",
  activeKey,
  defaultActiveKey,
  onChange,
  direction = "ltr",
  align = "left",
  className = "",
  testId,
  headerOnly = false,
  ...props
}) => {
  const tabsClass = [
    "tabs",
    `tabs--${variant}`,
    `tabs--${size}`,
    `tabs--align-${align}`,
    headerOnly && "tabs--header-only",
    className,
  ]
    .filter(Boolean)
    .join(" ");

  // 配置化方式：使用items数组
  if (items && items.length > 0) {
    return (
      <AntdMobileTabs
        className={tabsClass}
        activeKey={activeKey}
        defaultActiveKey={defaultActiveKey}
        onChange={onChange}
        direction={direction}
        data-testid={testId}
        {...props}
      >
        {items.map(item => (
          <AntdMobileTabs.Tab
            key={item.key}
            title={renderTitleWithBadge(item.title, item.badgeContent)}
            disabled={item.disabled}
            forceRender={item.forceRender}
            destroyOnClose={item.destroyOnClose}
          >
            {item.children !== undefined ? item.children : <div />}
          </AntdMobileTabs.Tab>
        ))}
      </AntdMobileTabs>
    );
  }

  // JSX方式：使用Tab子组件
  return (
    <AntdMobileTabs
      className={tabsClass}
      activeKey={activeKey}
      defaultActiveKey={defaultActiveKey}
      onChange={onChange}
      direction={direction}
      data-testid={testId}
      {...props}
    >
      {children}
    </AntdMobileTabs>
  );
};

// 将 Tab 附加到 Tabs 组件上
export const Tabs = Object.assign(TabsComponent, { Tab });

export default Tabs;
